import classNames from "classnames";
import React, { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { get } from "lodash";
import { useForm, useFormState } from "react-final-form";
import { findDictionaryItemByValue, recipeParameterUnitsType } from "shared/rsDictionary";
import IconButton from "components/IconButton";
import Input from "rs-components/Input";
import SelectInput from "rs-components/Input/components/SelectInput";
import SelectItemRenderer from "rs-components/Input/components/SelectInput/components/SelectItemRenderer";
import TimingDialog from "../../../SpaceViewerElement/components/TimingDialog";
import LineTypeIndicator from "../../../LineTypeIndicator";
import { viewTypeOptions } from "../../../ProcessDataViewerElement/components/SourceRow";
import { TimingFormFields } from "rs-components/TimingFields";
import ResultElementBaseSourceSelect from "../../../ResultElementBaseSourceSelect";
import { prepareProcessDataViewerSourceDataForSelect, } from "../../../ResultElementBaseSourceSelect/utils";
import { parameterTypesSelector, processVariablesOptionsSelector, processVariablesSelector, } from "store/global/global.selectors";
import { persistedInsightsDataSelector, workspaceInsightsSelector, workspaceOptimizationsSelector, workspaceRecipesSelector, workspaceSimulationsSelector, } from "store/rs-workspace/selectors/rs-workspace.selectors";
import { serverErrorsSelector } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { calculationGraphElementSourceResultSelector, getActiveResultSetIdSelector, } from "store/rs-workspace/selectors/results.selectors";
import { ProcessDataViewerSourceVariableType, ResultElementSourceType, } from "store/rs-workspace/rs-workspace.types";
import { parseComplexUnitToHtml } from "store/recipe/custom-variables.utils";
import { SourceFields, SourceSourceFields } from "../../../ProcessDataViewerElement/types";
import { RequestTemplate } from "store/knowledge-base-item/knowledge-base-item.types";
import { getRecipeParameterUnitsItems, getSimulationTimestampOptions, getSourceLineColor, getTimingSummaryText, } from "store/rs-workspace/utils/results.utils";
import { getErrorByPropertyName } from "store/knowledge-base-item/knowledge-base-item.utils";
import { getInsightSourceName, getRecipesCustomVariables, getSimulationSourceName, } from "store/rs-workspace/utils/scenario.utils";
import { isNotEmpty } from "utils/validate";
import DeleteIcon from "icons/delete.svg";
import * as styles from "../../styles.module.scss";
const SourceRow = ({ name, source, onRemoveSource, restrictions, index, elementData }) => {
    const form = useForm();
    const formState = useFormState();
    const [sourceDialogOpen, setSourceDialogOpen] = useState(false);
    const [timingDialogOpen, setTimingDialogOpen] = useState(false);
    const resultSetId = useSelector(getActiveResultSetIdSelector);
    const recipes = useSelector(workspaceRecipesSelector);
    const simulations = useSelector(workspaceSimulationsSelector);
    const optimizations = useSelector(workspaceOptimizationsSelector);
    const insights = useSelector(workspaceInsightsSelector);
    const persistedInsightsData = useSelector(persistedInsightsDataSelector);
    const parameterTypes = useSelector(parameterTypesSelector);
    const processVariables = useSelector(processVariablesSelector);
    const processVariableOptionsList = useSelector(processVariablesOptionsSelector);
    const sourceResult = useSelector(calculationGraphElementSourceResultSelector({ resultSetId, resultSetElementId: elementData.id }, elementData.type, source.id));
    const serverErrors = useSelector(serverErrorsSelector(RequestTemplate.LINE_GRAPH_ELEMENT_PUT));
    const { Insight: insightRestrictions = {}, Simulation: simulationRestrictions = {}, ...sourceRestrictions } = useMemo(() => restrictions?.Source || {}, [restrictions]);
    const processVariableOptions = useMemo(() => processVariableOptionsList.map((item) => ({
        ...item,
        variableType: ProcessDataViewerSourceVariableType.ProcessVariable,
    })), [processVariableOptionsList]);
    const error = useMemo(() => {
        if (!formState.pristine) {
            return "";
        }
        return sourceResult?.error || sourceResult?.sourceError;
    }, [formState.pristine, sourceResult?.error, sourceResult?.sourceError]);
    const timingDialogRecipeId = useMemo(() => {
        const isSimulation = source?.source?.type === ResultElementSourceType.Simulation;
        if (!isSimulation) {
            return undefined;
        }
        return simulations.find(({ id }) => id === source?.source?.[SourceSourceFields.simulationId])?.recipeId;
    }, [source, simulations]);
    const customVariablesList = useMemo(() => {
        const { customVariableId, simulationId } = { ...source?.source };
        const recipeId = simulations.find(({ id }) => id === simulationId)?.recipeId;
        return getRecipesCustomVariables(recipes, [recipeId], ({ recordInData, formula, id }) => isNotEmpty(customVariableId)
            ? Boolean(recordInData) && (!!formula?.complete || id === customVariableId)
            : Boolean(recordInData) && !!formula?.complete);
    }, [recipes, simulations, source]);
    const variablesOptions = useMemo(() => {
        const { customVariableId } = { ...source?.source };
        const customVariableRegularOptions = customVariablesList?.map(({ id, name, formula }) => {
            const unitHtml = parseComplexUnitToHtml(formula?.configuredUnits?.complexValue)?.html;
            return {
                value: id ?? 0,
                units: unitHtml,
                label: name ?? "",
                variableType: ProcessDataViewerSourceVariableType.Custom,
                disabled: isNotEmpty(customVariableId) && id === customVariableId && !formula?.complete,
            };
        });
        const customVariablesOptions = customVariableRegularOptions.length > 0
            ? [
                {
                    value: "Custom Variables",
                    label: "Custom Variables",
                    variableType: ProcessDataViewerSourceVariableType.Custom,
                    isGroupName: true,
                    disabled: true,
                },
                ...customVariableRegularOptions,
            ]
            : [];
        if (source?.source?.type === ResultElementSourceType.Simulation) {
            return [...processVariableOptions, ...customVariablesOptions];
        }
        return [...processVariableOptions, ...customVariablesOptions];
    }, [customVariablesList, processVariableOptions, source?.source]);
    const isInsight = useMemo(() => get(formState.values, `${name}.${SourceFields.source}.${SourceSourceFields.type}`) ===
        ResultElementSourceType.Insight, [formState.values, name]);
    const sourceSelected = useMemo(() => !!get(formState.values, `${name}.${SourceFields.source}`), [formState.values, name]);
    const unitsOptions = useMemo(() => {
        const selectedUnit = source?.source?.[SourceSourceFields.units]?.value;
        const options = getRecipeParameterUnitsItems("", selectedUnit, parameterTypes, [], true);
        return options.filter(({ value }) => value !== selectedUnit);
    }, [parameterTypes, source]);
    const timestampOptions = useMemo(() => getSimulationTimestampOptions(source?.source?.[SourceSourceFields.simulationId] ?? 0, simulations, recipes), [recipes, simulations, source]);
    const timingSummaryText = useMemo(() => getTimingSummaryText(get(formState.values, `${name}.${SourceFields.source}.${SourceFields.timing}`) || null, timestampOptions, processVariables, customVariablesList), [timestampOptions, processVariables, customVariablesList, formState.values, name]);
    const onSourceChange = useCallback((values) => {
        let sourceSourcePayload = {};
        let sourceName = "";
        const getSourceSourceBasePayload = (isSourceChanged) => ({
            ...(isSourceChanged
                ? {
                    ...source?.source,
                    [SourceSourceFields.variableType]: null,
                    [SourceSourceFields.timing]: null,
                    [SourceSourceFields.customVariableId]: null,
                    [SourceSourceFields.units]: null,
                    [SourceSourceFields._variableName]: null,
                }
                : source?.source),
            [SourceSourceFields._sourceName]: sourceName,
            [SourceSourceFields.type]: values.type,
        });
        const conditions = [source[SourceFields.source]?.type !== values.type];
        switch (values.type) {
            case ResultElementSourceType.Simulation: {
                const { simulationId, adjustmentId, optimizationId } = values;
                conditions.push(source?.source?.[SourceSourceFields.simulationId] !== simulationId);
                sourceName = getSimulationSourceName(simulations, optimizations, simulationId, optimizationId, adjustmentId);
                sourceSourcePayload = {
                    ...getSourceSourceBasePayload(conditions.some(Boolean)),
                    [SourceSourceFields.simulationId]: simulationId,
                    [SourceSourceFields.adjustmentId]: adjustmentId,
                    [SourceSourceFields.optimizationId]: optimizationId,
                };
                break;
            }
            case ResultElementSourceType.Insight: {
                const { insightId, inputSetId, output } = values;
                conditions.push(source?.source?.[SourceSourceFields.insightId] !== insightId);
                sourceName = getInsightSourceName(insights, persistedInsightsData, processVariables, insightId, inputSetId, output, recipes);
                sourceSourcePayload = {
                    ...getSourceSourceBasePayload(conditions.some(Boolean)),
                    [SourceSourceFields.simulationId]: null,
                    [SourceSourceFields.optimizationId]: null,
                    [SourceSourceFields.processDataId]: null,
                    [SourceSourceFields.insightId]: insightId,
                    [SourceSourceFields.inputSetId]: inputSetId,
                    [SourceSourceFields.outputId]: output.id,
                };
                break;
            }
        }
        form.change(name, {
            ...source,
            source: sourceSourcePayload,
        });
        setSourceDialogOpen(false);
    }, [
        source,
        form,
        name,
        simulations,
        optimizations,
        insights,
        persistedInsightsData,
        processVariables,
        recipes,
    ]);
    const onVariableChange = useCallback((option) => {
        const defaultUnit = findDictionaryItemByValue(recipeParameterUnitsType, option.units);
        let fieldsToReset = [];
        let targetField = "";
        form.batch(() => {
            form.change(`${name}.${SourceFields.source}.${SourceSourceFields._variableName}`, option);
            form.change(`${name}.${SourceFields.source}.${SourceSourceFields.units}`, defaultUnit);
            if (option?.variableType === ProcessDataViewerSourceVariableType.Custom) {
                fieldsToReset = [SourceSourceFields.variableType];
                targetField = SourceSourceFields.customVariableId;
            }
            if (option?.variableType === ProcessDataViewerSourceVariableType.ProcessVariable) {
                fieldsToReset = [SourceSourceFields.customVariableId];
                targetField = SourceSourceFields.variableType;
            }
            fieldsToReset.forEach((field) => form.change(`${name}.${SourceFields.source}.${field}`, null));
            form.change(`${name}.${SourceFields.source}.${targetField}`, option?.value);
        });
    }, [form, name]);
    const onTimingFieldsChange = useCallback((timingValues) => {
        form.change(`${name}.${SourceFields.source}.${SourceFields.timing}`, timingValues);
        setTimingDialogOpen(false);
    }, [name, form]);
    return (React.createElement("div", { className: classNames(styles.row, styles.editMode, { [styles.errored]: !!error }) },
        React.createElement("div", { className: classNames(styles.cell, styles.source) },
            React.createElement(Input, { name: `${name}.${SourceFields.source}.${SourceSourceFields._sourceName}`, component: SelectInput, items: [{}], placeholder: source?.[SourceFields.source]?.[SourceSourceFields._sourceName] ?? "Select", itemRenderer: SelectItemRenderer, restrictions: sourceRestrictions, classes: {
                    container: styles.selectWrapper,
                    component: styles.select,
                    error: styles.error,
                }, disableFormChange: true, popoverProps: { onInteraction: setSourceDialogOpen }, externalError: error ?? "" }),
            sourceDialogOpen && (React.createElement(ResultElementBaseSourceSelect, { title: "Data source", allowedTypes: [ResultElementSourceType.Simulation, ResultElementSourceType.Insight], initialValues: prepareProcessDataViewerSourceDataForSelect(source), onSubmit: onSourceChange, onClose: () => setSourceDialogOpen(false), withSimulationOptimization: true, restrictions: {
                    Insight: insightRestrictions,
                    Simulation: simulationRestrictions,
                    Type: sourceRestrictions,
                }, classes: { target: styles.sourceDialogTarget } }))),
        React.createElement("div", { className: classNames(styles.cell, styles.variable) }, !isInsight && (React.createElement(Input, { name: `${name}.${SourceFields.source}.${SourceSourceFields._variableName}`, component: SelectInput, items: variablesOptions, placeholder: "Select", itemRenderer: SelectItemRenderer, classes: {
                container: styles.selectWrapper,
                component: styles.select,
            }, restrictions: simulationRestrictions?.VariableType, disableFormChange: true, onChange: onVariableChange, disabled: !sourceSelected }))),
        React.createElement("div", { className: classNames(styles.cell, styles.unit) }, !isInsight && (React.createElement(Input, { name: `${name}.${SourceFields.source}.${SourceSourceFields.units}`, component: SelectInput, items: unitsOptions, itemRenderer: SelectItemRenderer, classes: {
                container: styles.selectWrapper,
                component: styles.select,
            }, restrictions: simulationRestrictions?.Units, placeholder: "Select" }))),
        React.createElement("div", { className: classNames(styles.cell, styles.timing) }, !isInsight && (React.createElement("div", { className: styles["timing-container"] },
            timingDialogOpen && (React.createElement(TimingDialog, { name: "_timing", timestampOptions: timestampOptions, onSubmit: (values) => onTimingFieldsChange(values?._timing), onClose: () => setTimingDialogOpen(false), timing: source?.source?.[SourceSourceFields.timing] ?? {}, selectedVariable: {
                    label: "",
                    value: source?.source?.[SourceSourceFields.variableType] ?? "",
                }, restrictions: simulationRestrictions?.Timing, recipeId: timingDialogRecipeId })),
            React.createElement("div", null,
                React.createElement(Input, { name: `${name}.${SourceFields.source}.${SourceSourceFields.timing}.${TimingFormFields.timestampType}`, component: SelectInput, itemRenderer: TimingDialog, placeholder: timingSummaryText || "Not specified", showTooltipIfDisabled: true, disabled: !source?.source?.variableType && !source?.source?.customVariableId, classes: {
                        container: styles.selectWrapper,
                        component: styles.select,
                    }, disableFormChange: true, popoverProps: { onInteraction: setTimingDialogOpen }, externalError: getErrorByPropertyName(`Request.${name}.Source.Timing`, serverErrors)?.text, showErrorIfUntouched: true, restrictions: { IsNecessary: true }, errorLabel: "Timestamp" }))))),
        React.createElement("div", { className: classNames(styles.cell, styles.line) },
            React.createElement(LineTypeIndicator, { color: getSourceLineColor(get(formState.values, `${name}.${SourceFields.color}`), index), onChange: (color) => form.change(`${name}.${SourceFields.color}`, color) })),
        React.createElement("div", { className: classNames(styles.cell, styles.viewType) },
            React.createElement(Input, { name: `${name}.${SourceFields.viewType}`, component: SelectInput, items: viewTypeOptions, itemRenderer: SelectItemRenderer, valueFormatter: ({ value }) => value, classes: {
                    container: styles.selectWrapper,
                    component: styles.select,
                }, restrictions: {}, placeholder: "Select" })),
        React.createElement("div", { className: classNames(styles.cell, styles.delete) },
            React.createElement(IconButton, { icon: React.createElement(DeleteIcon, null), title: "Delete", onClick: onRemoveSource }))));
};
export default SourceRow;
